import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from '../Toast';
import { api } from '../../services/api';
import socketService from '../../services/socket';

/**
 * Shared chat orchestration for the trainer/admin and client surfaces.
 * Both the floating bubble (FloatingChatWidget / ClientFloatingChatWidget)
 * and the dedicated `/dashboard/messages` page consume these hooks so
 * orchestration logic stays in one place.
 *
 * Each hook owns:
 *   - data loading (initial + on-demand refresh)
 *   - the live `socketService.onUnreadCountUpdated` subscription
 *   - the find-or-create conversation flow
 *   - mark-as-read on conversation select
 *   - the email-notify flow with deduped `notifiedMessageIds` (trainer only)
 *
 * Each hook does NOT own:
 *   - widget-only chrome (open/close, dragging, ESC handling) — those stay
 *     in the floating widgets
 *   - polling intervals — left to the consumer because cadence depends on
 *     surface (the bubble polls on open; the page is always "open")
 */

/* ------------------------------------------------------------------ *
 * Trainer / admin
 * ------------------------------------------------------------------ */
export function useTrainerChat({ currentUser, clients, getAllOnsiteClients }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [lastNotifiedMessages, setLastNotifiedMessages] = useState({});
  const [activeNotifiedMessageIds, setActiveNotifiedMessageIds] = useState([]);
  // The thread on screen. Read by the unread refresh below, which runs from a
  // socket event and would otherwise see a stale selection.
  const openConversationIdRef = useRef(null);
  useEffect(() => {
    openConversationIdRef.current = selectedConversation?._id || null;
  }, [selectedConversation]);

  const loadConversations = useCallback(async () => {
    if (!currentUser) return;
    try {
      setLoading(true);
      const convs = await api.getConversations();
      setConversations(convs);
    } catch (err) {
      console.error('Error loading conversations:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const refreshUnreadCounts = useCallback(async () => {
    if (!currentUser) return;
    try {
      const convs = await api.getConversations();
      // A new message in the thread the coach is looking at is read (the
      // open <Chat> marks it), but this fetch races that call and left a
      // stale "1" on the open thread.
      const open =
        document.visibilityState === 'visible'
          ? openConversationIdRef.current
          : null;
      setConversations(
        open
          ? convs.map((c) => (c._id === open ? { ...c, unreadCount: 0 } : c))
          : convs
      );
    } catch (err) {
      console.error('Error refreshing conversations:', err);
    }
  }, [currentUser]);

  // Initial load on mount so the bubble's unread badge is accurate without
  // requiring the user to open the bubble first. (Was lazy before, which
  // hid the badge until first click.)
  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Backfill the clients list if the parent didn't already.
  useEffect(() => {
    if (!currentUser) return;
    if (!clients || clients.length === 0) {
      getAllOnsiteClients?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Live unread-count updates over websocket.
  useEffect(() => {
    if (!currentUser) return;
    socketService.connect();
    const handler = () => refreshUnreadCounts();
    socketService.onUnreadCountUpdated(handler);
    return () => {
      if (socketService.socket) {
        socketService.socket.off('unread_count_updated', handler);
      }
    };
  }, [currentUser, refreshUnreadCounts]);

  // Join every conversation's socket room so the trainer receives
  // `unread_count_updated` events from clients regardless of whether
  // the trainer has that specific conversation open. The server
  // emits to `io.to(conversationId)` only — without these joins, the
  // bubble badge would only react to messages on whichever
  // conversation <Chat> currently has open. Closed bubble / different
  // conversation selected → silent.
  //
  // Tradeoff: a trainer with 30 clients joins 30 rooms. Socket.io
  // handles this fine — each room is a Set membership, not a
  // separate socket. Re-runs whenever the conversations list changes
  // (new client, deleted conversation) so newly-created threads also
  // pick up real-time updates without a reload. Re-joins on socket
  // reconnect so a network blip doesn't silently drop updates.
  useEffect(() => {
    if (!conversations || conversations.length === 0) return;
    socketService.connect();
    const ids = conversations.map((c) => c._id).filter(Boolean);
    const joinAll = () => ids.forEach((id) => socketService.joinConversation(id));
    if (socketService.socket?.connected) {
      joinAll();
    } else if (socketService.socket) {
      socketService.socket.once('connect', joinAll);
    }
    if (socketService.socket) {
      socketService.socket.on('connect', joinAll);
    }
    return () => {
      if (socketService.socket) {
        socketService.socket.off('connect', joinAll);
      }
      // Don't `leaveConversation` on cleanup — <Chat> may still be
      // mounted on one of these threads and depends on the room
      // membership for live message delivery.
    };
  }, [conversations]);

  const unreadCount = useMemo(
    () => conversations.reduce((n, c) => n + (c.unreadCount || 0), 0),
    [conversations]
  );

  const handleConversationSelect = useCallback(
    async (conversation) => {
      setSelectedConversation(conversation);
      setActiveNotifiedMessageIds(lastNotifiedMessages[conversation._id] || []);
      try {
        await api.markAsRead(conversation._id);
        setConversations((prev) =>
          prev.map((c) =>
            c._id === conversation._id ? { ...c, unreadCount: 0 } : c
          )
        );
      } catch (err) {
        console.error('Error marking conversation as read:', err);
      }
    },
    [lastNotifiedMessages]
  );

  const createConversation = useCallback(
    async (client) => {
      if (!client?.user?.uid) {
        throw new Error(
          `Client Firebase UID is missing for client: ${
            client?.user?.name || client?.user?.email || 'unknown'
          }`
        );
      }
      const participants = [
        {
          userId: currentUser.uid,
          userName: currentUser?.name || 'Admin',
          role: 'admin',
        },
        {
          userId: client.user.uid,
          userName:
            `${client.user.name || ''} ${client.user.lastName || ''}`.trim() ||
            client.user.email ||
            'Client',
          role: 'client',
        },
      ];
      const newConv = await api.createConversation(participants);
      setConversations((prev) => [...prev, newConv]);
      handleConversationSelect(newConv);
      return newConv;
    },
    [currentUser, handleConversationSelect]
  );

  const handleNewConversation = useCallback(
    async (client) => {
      try {
        const existing = conversations.find((conv) =>
          conv.participants.some((p) => p.userId === client.user?.uid)
        );
        if (existing) {
          handleConversationSelect(existing);
          return;
        }
        await createConversation(client);
      } catch (err) {
        console.error('Error in handleNewConversation:', err);
        toast.error('Failed to create conversation. Please try again.');
      }
    },
    [conversations, handleConversationSelect, createConversation]
  );

  // Keep activeNotifiedMessageIds in sync when a different conversation
  // becomes selected. Mirrors the widget's previous effect.
  useEffect(() => {
    if (selectedConversation?._id) {
      setActiveNotifiedMessageIds(
        lastNotifiedMessages[selectedConversation._id] || []
      );
    } else {
      setActiveNotifiedMessageIds([]);
    }
  }, [selectedConversation, lastNotifiedMessages]);

  const selectedParticipant = useMemo(() => {
    if (!selectedConversation || !currentUser) return null;
    return selectedConversation.participants?.find(
      (p) => p?.userId !== currentUser.uid
    );
  }, [selectedConversation, currentUser]);

  const refreshAfterNotify = useCallback(async () => {
    try {
      const convs = await api.getConversations();
      setConversations(convs);
      if (selectedConversation?._id) {
        const updated = convs.find((c) => c._id === selectedConversation._id);
        if (updated) setSelectedConversation(updated);
      }
    } catch (err) {
      console.error('Error refreshing after notify:', err);
    }
  }, [selectedConversation]);

  const handleSendNotification = useCallback(
    async (force = false, sourceMessage = null) => {
      if (!selectedConversation || !selectedParticipant) {
        toast.error('Unable to determine conversation participant.');
        return;
      }
      try {
        setNotifyLoading(true);
        const shouldForce = force || !!selectedConversation?.emailNotified;
        const response = await api.notifyConversationParticipant(
          selectedConversation._id,
          selectedParticipant.userId,
          { force: shouldForce }
        );
        const recipientEmail = response?.email;
        const snippet =
          sourceMessage?.text && sourceMessage.text.length > 0
            ? ` referencing "${sourceMessage.text.slice(0, 80)}${
                sourceMessage.text.length > 80 ? '…' : ''
              }"`
            : '';
        toast.success(
          recipientEmail
            ? `Email sent to ${recipientEmail}${snippet}.`
            : `Email notification sent${snippet}.`
        );
        const notifiedId = sourceMessage?._id || sourceMessage?.tempId || null;
        if (notifiedId) {
          setLastNotifiedMessages((prev) => {
            const existing = prev[selectedConversation._id] || [];
            return {
              ...prev,
              [selectedConversation._id]: existing.includes(notifiedId)
                ? existing
                : [...existing, notifiedId],
            };
          });
          setActiveNotifiedMessageIds((prev) =>
            prev.includes(notifiedId) ? prev : [...prev, notifiedId]
          );
        }
        await refreshAfterNotify();
      } catch (err) {
        console.error('Error sending notification email:', err);
        toast.error(
          err?.response?.data?.message ||
            err.message ||
            'Failed to send email notification.'
        );
      } finally {
        setNotifyLoading(false);
      }
    },
    [selectedConversation, selectedParticipant, refreshAfterNotify]
  );

  const canSendManualEmail =
    currentUser?.role && currentUser.role !== 'client';

  return {
    conversations,
    setConversations,
    selectedConversation,
    setSelectedConversation,
    loading,
    notifyLoading,
    activeNotifiedMessageIds,
    selectedParticipant,
    canSendManualEmail,
    unreadCount,
    loadConversations,
    refreshUnreadCounts,
    handleConversationSelect,
    handleNewConversation,
    handleSendNotification,
  };
}

/* ------------------------------------------------------------------ *
 * Client
 * ------------------------------------------------------------------ */
export function useClientChat({ currentUser }) {
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const loadOrCreateConversation = useCallback(async () => {
    if (!currentUser) return;
    try {
      setLoading(true);
      const conversations = await api.getConversations();

      const adminConv = conversations.find((conv) => {
        if (!Array.isArray(conv.participants)) return false;
        if (conv.participants.length !== 2) return false;
        const hasClient = conv.participants.some(
          (p) => p.userId === currentUser.uid && p.role === 'client'
        );
        const hasAdmin = conv.participants.some((p) =>
          ['admin', 'trainer', 'platform_admin', 'organization_admin'].includes(
            p.role
          )
        );
        return hasClient && hasAdmin;
      });

      if (adminConv) {
        setConversation(adminConv);
        setUnreadCount(adminConv.unreadCount || 0);
        return;
      }

      const adminUserId = currentUser.createdBy;
      if (!adminUserId) {
        console.error('No assigned trainer for this client.');
        setConversation(null);
        return;
      }
      const newConv = await api.createConversation([
        {
          userId: currentUser.uid,
          userName:
            currentUser.name ||
            `${currentUser.name || ''} ${currentUser.lastName || ''}`.trim(),
          role: 'client',
        },
        // The API names participants from their user records; this label
        // only covers the round-trip.
        {
          userId: adminUserId,
          userName: currentUser.coach?.name || 'Coach',
          role: 'admin',
        },
      ]);
      setConversation(newConv);
      setUnreadCount(newConv.unreadCount || 0);
    } catch (err) {
      console.error('Error loading conversation:', err);
      setConversation(null);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadOrCreateConversation();
  }, [loadOrCreateConversation]);

  // Live unread updates for THIS conversation only. Two pieces:
  //
  //   1. JOIN THE ROOM. The server emits `unread_count_updated` to
  //      the conversation room only (`io.to(conversationId).emit`),
  //      so the client must be in that room to receive the event.
  //      <Chat> joins on mount, but it only mounts when the bubble
  //      is OPEN — meaning closed-bubble badges never updated. We
  //      join here as soon as the conversation is loaded so the
  //      badge tracks new messages even with the bubble collapsed.
  //
  //   2. REFRESH ON EVENT. The event payload only carries
  //      `{ conversationId, senderId }`, no count. We re-fetch the
  //      conversation list and pick up the fresh `unreadCount` —
  //      same pattern useTrainerChat uses.
  useEffect(() => {
    if (!conversation?._id) return;

    // Ensure the socket exists, then join the room. If the socket
    // is still mid-handshake, queue the join for the connect event.
    socketService.connect();
    const join = () => socketService.joinConversation(conversation._id);
    if (socketService.socket?.connected) {
      join();
    } else if (socketService.socket) {
      socketService.socket.once('connect', join);
    }

    const handler = async (data) => {
      if (data?.conversationId !== conversation._id) return;
      try {
        const conversations = await api.getConversations();
        const fresh = (conversations || []).find(
          (c) => String(c._id) === String(conversation._id)
        );
        if (fresh) setUnreadCount(fresh.unreadCount || 0);
      } catch (_) {
        // best-effort; the next bubble open will reconcile via
        // `loadOrCreateConversation`.
      }
    };
    socketService.onUnreadCountUpdated(handler);
    return () => {
      if (socketService.socket) {
        socketService.socket.off('unread_count_updated', handler);
        // Don't `leaveConversation` on cleanup — <Chat> may also be
        // mounted (bubble open) and the room membership is shared
        // across both. Leaving here would yank the rug on real-time
        // message updates inside the open chat.
      }
    };
  }, [conversation?._id]);

  const markRead = useCallback(async () => {
    if (!conversation?._id) return;
    try {
      await api.markAsRead(conversation._id);
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking conversation as read:', err);
    }
  }, [conversation]);

  const trainerName = useMemo(() => {
    const coachName = currentUser?.coach?.name || 'Your coach';
    if (!conversation?.participants) return coachName;
    const trainer = conversation.participants.find((p) =>
      ['admin', 'trainer', 'platform_admin', 'organization_admin'].includes(
        p.role
      )
    );
    return trainer?.userName || trainer?.user?.name || coachName;
  }, [conversation, currentUser]);

  return {
    conversation,
    setConversation,
    loading,
    unreadCount,
    setUnreadCount,
    loadOrCreateConversation,
    markRead,
    trainerName,
  };
}
